<svelte:options
  customElement={{
    tag: 'dynamic-svelte-site-search',
    props: {
      siteId: { type: 'String' },
      baseApiUrl: { type: 'String' },
      apiUrl: { type: 'String' },
      language: { type: 'String' },
      dictionary: { type: 'Object' },
    },
    shadow: 'none',
  }}
/>

<script>
  /* eslint-disable compat/compat */

  import { SvelteURLSearchParams } from 'svelte/reactivity';
  import Pagination from '../pagination/pagination.svelte';
  import Icon from '../icon/icon.svelte';

  let { siteId, language = 'en-US', dictionary, baseApiUrl = '' } = $props();

  let apiUrl = $derived(baseApiUrl + '/api/sitesearch/' + siteId);

  const SEARCH_PAGE_URL =
    document.querySelector('[data-search-bar]').attributes['data-search-url'].value;
  const COUNT_PER_PAGE = 12;

  let urlParams = new SvelteURLSearchParams(window.location.search);
  let currentPage = $state(parseInt(urlParams.get('page') ?? '1') - 1);
  let searchInput = $state(urlParams.get('search'));

  let filters = $state({
    sort: urlParams.get('sort') || 'wgt',
  });

  function onSearchSubmit(e) {
    e.preventDefault();
    currentPage = 0;
    urlParams.set('search', searchInput);
    const newPath = SEARCH_PAGE_URL + '?' + urlParams.toString();
    history.pushState({}, '', newPath);
    fetchSearch();
  }

  function getParams() {
    const params = new SvelteURLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      params.append(key, value);
    });

    params.append('page', currentPage);
    params.append('count', COUNT_PER_PAGE);
    params.append('lang', language); // Versive add current language

    if (searchInput) {
      params.append('search', searchInput);
    }

    return params;
  }

  let finishedInitialSearch = $state(false);
  let listingItems = $state([]);
  let totalResults = $state(0);

  async function fetchSearch() {
    const params = getParams();
    let url = apiUrl + '?' + params.toString();

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      finishedInitialSearch = true;

      listingItems = data.items;
      totalResults = data.totalCount;
    } catch (error) {
      console.error('Fetch error:', error);
    }
  }

  function onPaginationClick(number) {
    currentPage = number;
    urlParams.set('page', number + 1);
    const newPath = SEARCH_PAGE_URL + '?' + urlParams.toString();
    history.pushState({}, '', newPath);
    fetchSearch();
  }

  function onFiltersChange() {
    currentPage = 0;
    urlParams.set('page', 1);

    Object.entries(filters).forEach(([key, value]) => {
      urlParams.set(key, value);
    });

    updateUrl();
  }

  function updateUrl() {
    const newPath = SEARCH_PAGE_URL + '?' + urlParams.toString();
    history.pushState({}, '', newPath);
    fetchSearch();
  }

  fetchSearch();
</script>

<section
  class="search-hero section dynamic-site-search"
  data-sitesearch
  data-siteid={siteId}
  data-lang={language}
  data-size="6"
>
  <h1 class="search-hero__title">{dictionary.Search}</h1>
  <div class="search-form">
    <form class="search-form__form" data-search-form onsubmit={onSearchSubmit}>
      <Icon
        class="icon hidden-mobile"
        width="16"
        height="20"
        aria-label={dictionary.Search}
        icon="search"
      />
      <input
        name="search"
        class="input"
        aria-label="Search"
        type="text"
        bind:value={searchInput}
        data-search-input
      />
      <button class="button button__primary button__large" type="submit">
        <span class="hidden-mobile">{dictionary.Search}</span>
        <Icon
          class="icon hidden-desktop"
          width="16"
          height="20"
          aria-label={dictionary.Search}
          icon="search"
        />
      </button>
    </form>
  </div>

  <div class="search-filters">
    <div class="select-wrapper">
      <label for="sort" class="search-filters__label">{dictionary.SortBy}:</label>
      <select
        id="sort"
        data-search-filter
        class="search-filters__dropdown select"
        name="sort"
        bind:value={filters.sort}
        onchange={onFiltersChange}
      >
        <option value="wgt">{dictionary.SortByRel}</option>
        <option value="dtdesc">{dictionary.LatesttoOldest}</option>
        <option value="dtasc">{dictionary.OldesttoLatest}</option>
      </select>
    </div>
  </div>
</section>

<section class="search-content section" data-search aria-live="polite">
  <!-- Pagination -->
  {#if finishedInitialSearch}
    {#if totalResults === 0}
      <div
        class="no-result search-listing__no-results"
        style="padding: 50px;min-height:150px;text-align:center;"
      >
        {dictionary.NoResults}
      </div>
    {:else}
      <ul class="search-listing" data-search-listing>
        {#each listingItems as item (item.id)}
          <li class="search-listing__item">
            <div class="search-listing__image-wrapper">
              {#if item.image}
                <img src={item.image} alt={item.title} />
              {/if}
            </div>

            <div class="search-listing__content">
              <div class="search-listing__heading">
                {#if item.category}
                  <div class="search-listing__category">{item.category}</div>
                {/if}
                <div class="search-listing__title">
                  <a href={item.link} class="search-listing__link">{item.title}</a>
                </div>
              </div>
              <div class="search-listing__copy">{item.copy}</div>
              <div class="search-listing__tags">
                <ul class="search-listing__tags-list">
                  {#each item.tags as tag, i (i)}
                    <li class="search-listing__tag-item">
                      <a href={tag.link} class="search-listing__tag-link">
                        {tag.name}
                      </a>
                    </li>
                  {/each}
                </ul>
              </div>
            </div>
          </li>
        {/each}
      </ul>

      <Pagination
        totalCount={totalResults}
        countPerPage={COUNT_PER_PAGE}
        {currentPage}
        {dictionary}
        onClick={onPaginationClick}
      />
    {/if}
  {/if}
</section>
